import React from 'react';
import {
  RedemptionTypeKey,
  RedemptionTypeStatus,
  RewardOptionsQuery$data,
} from './__generated__/RewardOptionsQuery.graphql';
import Notice from 'components/atoms/Notice/Notice';
import RedemptionOptions from 'components/organisms/RedemptionOptions/RedemptionOptions';
import A from 'components/atoms/A/A';
import Card from 'components/atoms/Card/Card';
import { Filters } from './RewardOptions';
import classes from './RewardOptions.module.scss';
import Txt from 'components/atoms/Txt/Txt';

export interface Props {
  redemptionOptions: RewardOptionsQuery$data['redemptionOptions'];
  filters: Partial<Filters>;
  currentRedemptionType:
    | {
        readonly linkNewAccountUrl: string | null;
        readonly name: RedemptionTypeKey;
        readonly status: RedemptionTypeStatus;
      }
    | undefined;
}

export default function OptionsPane({ redemptionOptions, filters, currentRedemptionType }: Props) {
  if (!redemptionOptions || !currentRedemptionType) {
    return (
      <Notice
        message="We are having problems loading cashouts at the moment. Please try again later."
        status="warning"
      />
    );
  }

  if (currentRedemptionType.status === 'REQUIRES_VERIFICATION') {
    return (
      <>
        <Txt component="p" bold>
          Looks like you need to link a PayPal account...
        </Txt>
        <Txt component="p">
          We want to make sure that you get your LifePoints rewards into your PayPal account as
          quickly as possible. We need you to link your PayPal account because we can then be sure
          that we are sending the rewards to the correct account.
        </Txt>
        <Txt component="p">
          Please head to the PayPal tab, press &apos;Link new account&apos;, and login via PayPal.
          It is completely secure, and all we need you to share is your email and address - we do
          not get access to your account. Please ensure that the account you have linked is verified
          by PayPal. To ensure your PayPal account remains up to date, you may be asked to refresh
          your linked PayPal again in the future.
        </Txt>
        <Txt component="p">
          Your rewards will arrive in your PayPal account within the next five days, so don&apos;t
          panic if you don&apos;t see your reward straight away.
        </Txt>
        <Txt component="p">
          If you have any issues doing this, please reach out to our friendly team via the{' '}
          <A href="https://lifepoints.zendesk.com/">Help Center</A>.
        </Txt>
      </>
    );
  }

  return (
    <RedemptionOptions filters={filters} redemptionOptionsRef={redemptionOptions}>
      {currentRedemptionType.linkNewAccountUrl && (
        <li className={classes.linkNewAccount}>
          <A href={currentRedemptionType.linkNewAccountUrl}>
            <Card className={classes.linkNewAccountCard}>Link new account</Card>
          </A>
        </li>
      )}
    </RedemptionOptions>
  );
}
